import React, {
  FunctionComponent,
  ReactElement,
  useEffect,
  useState,
} from 'react';
import Chart from 'react-apexcharts';
import styles from './BaseLine.module.scss';

import SelectInput from 'app/components/Form/Inputs/SelectInput';
import {
  CountryIcon,
  DateIcon,
  ExpandIcon,
  HashtagsIcon,
  PlatformIcon,
} from 'app/assets/images/icons';
import { Link } from 'react-router-dom';
import DotsMenu from 'app/components/DotsMenu';
import { nFormatter } from 'app/utils/numbersFormater';
import Loader from 'app/components/Loader';
import NoData from 'app/components/NoData';
import { _t } from 'app/utils/i18n';
import { loadBaseLineChartData } from 'app/Requests';
import CardTitle from 'app/components/CardTitle';
import { getFromLocalStorage } from 'app/utils/localStorage';
import { useQuery, useQueryClient } from 'react-query';
import ReactSelectAutocomplete from 'app/components/Form/Inputs/ReactSelectAutocomplete';
import { apiRoutes as hashtagsApiRoutes } from 'hashtags/utils/apiRoutes';
import { checkTranslated } from 'app/utils/helpers';
import ServerError from 'app/components/ErrorBoundary/ServerError';
import { useAppSettings } from 'app/context/settings/appSettings';

interface Props {
  chartData?: any;
  removeTooltip?: boolean;
  slugName?: string;
  baseLineTitle?: string;
  countryFilter?: boolean;
  hashtagFilter?: boolean;
  platformFilter?: boolean;
  timeIntervalFilter?: boolean;
  expandUrl?: string;
  nameOnly?: boolean;
  customFilters?: any;
  showDefaultTooltip?: boolean | undefined;
  compId: string;
  hideNames?: boolean;
  noDayInTimeInterval?: boolean;
  showCustomTooltip?: boolean;
  trendsVideoChannelTooltip?: boolean;
  tooltipTitle?: string[];
  additionalComponents?: ReactElement;
  isReport?: boolean;
  compareVideos?: boolean;
  withMonthlyTime?: boolean;
  withYearTime?: boolean;
  defaultTimeInterval?: 'DAY' | 'WEEK' | 'MONTH' | 'YEAR';
  hideIfNoData?: boolean;
}

const time_interval = [
  { label: _t('basic:day'), value: 'DAY' },
  { label: _t('basic:week'), value: 'WEEK' },
  { label: _t('basic:month'), value: 'MONTH' },
  { label: _t('basic:year'), value: 'YEAR' },
];

const no_day_time_interval = [
  { label: _t('basic:week'), value: 'WEEK' },
  { label: _t('basic:month'), value: 'MONTH' },
  { label: _t('basic:year'), value: 'YEAR' },
];

const month_time_interval = [
  { label: _t('basic:month'), value: 'MONTH' },
  { label: _t('basic:year'), value: 'YEAR' },
];
const year_time_interval = [{ label: _t('basic:year'), value: 'YEAR' }];
const BaseLine: FunctionComponent<Props> = ({
  chartData,
  baseLineTitle,
  removeTooltip,
  slugName,
  countryFilter,
  hashtagFilter,
  platformFilter,
  timeIntervalFilter,
  expandUrl,
  nameOnly,
  customFilters,
  compId,
  isReport,
  showDefaultTooltip,
  hideNames,
  noDayInTimeInterval,
  showCustomTooltip,
  trendsVideoChannelTooltip,
  tooltipTitle,
  additionalComponents,
  compareVideos,
  withMonthlyTime,
  withYearTime,
  defaultTimeInterval,
  hideIfNoData
}) => {
  const queryClient = useQueryClient();
  const { theme } = useAppSettings();

  const countriesOptions = queryClient.getQueryData('getCountries') as any;
  const platforms = queryClient.getQueryData('getPlatforms') as any;
  const defaultCountriesIds = countriesOptions
    ?.filter((option: any) => option?.default)
    ?.map((i: any) => i.value);
  useEffect(() => {
    setFilters({
      ...customFilters,
      limit: 5,
      time_interval: defaultTimeInterval
        ? defaultTimeInterval
        : noDayInTimeInterval
        ? 'WEEK'
        : withMonthlyTime
        ? 'MONTH'
        : withYearTime
        ? 'YEAR'
        : 'DAY',
      country_ids: countryFilter ? defaultCountriesIds : null,
    });
  }, [customFilters]);
  useEffect(() => {
    return () => {
      queryClient.removeQueries(['loadBaseLineChartData']);
    };
  }, [customFilters]);
  const [filters, setFilters] = useState({
    ...customFilters,
    limit: 5,
    time_interval: defaultTimeInterval
      ? defaultTimeInterval
      : noDayInTimeInterval
      ? 'WEEK'
      : withMonthlyTime
      ? 'MONTH'
      : withYearTime
      ? 'YEAR'
      : 'DAY',
    country_ids: countryFilter ? defaultCountriesIds : null,
  });

  const {
    isLoading,
    data: QData,
    isError,
  } = useQuery(
    ['loadBaseLineChartData', { slug: slugName, body: filters }],
    loadBaseLineChartData,
    { enabled: !!slugName }
  );

  const data = !!slugName
    ? QData
    : !!chartData
    ? chartData
    : { values: [], xaxis: [] };

  const noData = !data || data?.values?.length === 0;

  const handleChangeFilters = (name: string) => (option: any) => {
    if (Array.isArray(option)) {
      const values = option.map((item: any) => Number(item.value));
      setFilters((prevFilters: any) => ({ ...prevFilters, [name]: values }));
    } else {
      setFilters((prevFilters: any) => ({
        ...prevFilters,
        [name]: option.value,
      }));
    }
  };
  const tooltipContent = (
    seriesIndex: number,
    dataPointIndex: number,
    tooltipData: any
  ) => {
    if (showCustomTooltip || trendsVideoChannelTooltip) {
      tooltipData = showCustomTooltip ? data?.tooltip : data?.values?.[0];

      return `
      <div>
        <div style='flex-basis:unset'>
          <div class="color_name">
            <div
              class="colorBox"
              style="
                border-color: ${theme.colors.chartsColors[seriesIndex]};
                background-color: ${theme.colors.chartsColors[seriesIndex]};
                border-radius:50%
              "
            ></div>
            <div>${data?.xaxis?.[dataPointIndex]}</div>
          </div>
        </div>
      </div>
       ${
         tooltipData?.[dataPointIndex]?.length
           ? `                  
      <div>
      <div><h5>${tooltipTitle?.[0] ? tooltipTitle[0] : _t('videos:videos')}</h5>
      ${tooltipData?.[dataPointIndex]
        ?.map((item: any) => {
          return `<p>${checkTranslated(
            item?.name?.original,
            item?.name?.arabic
          )}<p>`;
        })
        ?.join(' ')}
      
      </div>
      <div><h5>${
        tooltipTitle?.[1] ? tooltipTitle[1] : _t('trends:total_views')
      }</h5>
      ${tooltipData?.[dataPointIndex]
        ?.map((item: any) => {
          return `<p>${nFormatter(item?.count, 2)}<p>`;
        })
        ?.join(' ')}</div>
      </div>`
           : trendsVideoChannelTooltip
           ? `                  
           <div>
           <div> 
           ${`<p>${checkTranslated(
             tooltipData?.name?.original,
             tooltipData?.name?.arabic
           )}<p>`}
           
           </div>
           <div> 
           <p>${nFormatter(tooltipData?.data?.[dataPointIndex], 2)}<p>
           </div>
           </div>`
           : compareVideos
           ? `${` <div> 
           <p>${nFormatter(
             data?.values?.[seriesIndex]?.data?.[dataPointIndex],
             2
           )}<p>
           </div>`}`
           : ''
       } `;
    } else {
      return ` <div>
        <div>
          <h5>${_t('trends:hashtags')}</h5>
          
          ${data?.values[seriesIndex].tooltip[dataPointIndex]
            ?.map((item: any) => {
              return `<p>${
                typeof item.name === 'string'
                  ? item.name
                  : checkTranslated(
                      tooltipData?.name?.original,
                      tooltipData?.name?.arabic
                    )
              }<p>`;
            })
            ?.join(' ')}
        </div>
        <div>
          <h5>${_t('trends:Mentions')}</h5>
          ${data?.values[seriesIndex].tooltip[dataPointIndex]
            ?.map((item: any) => {
              return `<p>${(nFormatter(item.mentions), 2)}<p>`;
            })
            ?.join(' ')}
        </div>
      </div>`;
    }
  };
  if (data?.is_disabled) return <></>;
  if (noData && hideIfNoData) return <></>
  return (
    <ServerError requiredApiCalls={[isError]}>
      <div id={compId} data-testid={compId}>
        {baseLineTitle && (
          <CardTitle
            title={baseLineTitle}
            compId={compId}
            hideIcon={isReport}
          />
        )}
        <div
          className={styles.tooltipContent}
          id={`${compId}-baseLine-content`}
          data-testid="baseLine-test-id"
        >
          {nameOnly ? (
            <div
              className={styles.toolBarNames}
              id={`${compId}-baseLine-legend`}
            >
              {data?.values?.map((val: any, i: any) => {
                return (
                  <div key={i} className={styles.specityChartHolder}>
                    <div className={styles.specifyChart}>
                      <div
                        className={styles.colorBox}
                        style={{
                          borderColor: theme.colors.chartsColors[i],
                          backgroundColor: theme.colors.chartsColors[i],
                        }}
                      />
                      <div>
                        {typeof val.name === 'string'
                          ? val.name
                          : checkTranslated(
                              val?.name?.original,
                              val?.name?.arabic
                            )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={styles.toolBar} id={`${compId}-baseLine-toolBar`}>
              <div className={styles.Filters}>
                {countryFilter && (
                  <div
                    className={styles.Select}
                    data-testid="countryFilter-test-id"
                  >
                    <SelectInput
                      icon={<CountryIcon />}
                      placeholder={_t('trends:Country')}
                      options={countriesOptions as any}
                      value={countryFilter as any}
                      isMulti
                      onChange={handleChangeFilters('country_ids')}
                      showDefaultValue
                      name={''}
                    />
                  </div>
                )}
                {hashtagFilter && (
                  <div
                    className={styles.Select}
                    data-testid="hashtagFilter-test-id"
                  >
                    <ReactSelectAutocomplete
                      moduleName={
                        hashtagsApiRoutes().TrendingHashtagAutoComplete
                      }
                      placeholder={_t('trends:hashtag')}
                      icon={<HashtagsIcon />}
                      onChange={handleChangeFilters('hashtag_ids')}
                      isMulti={true}
                      keyExtractor="value"
                    />
                  </div>
                )}
                {platformFilter && (
                  <div
                    className={styles.Select}
                    data-testid="platformFilter-test-id"
                  >
                    <SelectInput
                      icon={<PlatformIcon />}
                      placeholder={_t('trends:Platforms')}
                      options={platforms?.platformsOptions}
                      isMulti
                      onChange={handleChangeFilters('platform_ids')}
                      name={'baseline-select-platform'}
                    />
                  </div>
                )}
                {timeIntervalFilter && (
                  <div
                    className={styles.Select}
                    data-testid="timeIntervalFilter-test-id"
                  >
                    <SelectInput
                      icon={<DateIcon />}
                      placeholder={
                        defaultTimeInterval
                          ? _t(`basic:${defaultTimeInterval.toLowerCase()}`)
                          : noDayInTimeInterval
                          ? _t('basic:week')
                          : withMonthlyTime
                          ? _t('basic:month')
                          : withYearTime
                          ? _t('basic:year')
                          : _t('basic:day')
                      }
                      options={
                        noDayInTimeInterval
                          ? no_day_time_interval
                          : withMonthlyTime
                          ? month_time_interval
                          : withYearTime
                          ? year_time_interval
                          : time_interval
                      }
                      onChange={handleChangeFilters('time_interval')}
                      name={'baseline-select-time'}
                    />
                  </div>
                )}
                {!hideNames &&
                  data?.values?.map((val: any, i: any) => {
                    return (
                      <div
                        key={i}
                        className={styles.specityChartHolder}
                        data-testid="specityChartHolder"
                      >
                        <div className={styles.specifyChart}>
                          <div
                            className={styles.colorBox}
                            style={{
                              borderColor: theme.colors.chartsColors[i],
                              backgroundColor: theme.colors.chartsColors[i],
                            }}
                          ></div>
                          <div
                            style={{
                              fontSize: 10,
                            }}
                          >
                            {typeof val.name === 'string'
                              ? val.name
                              : checkTranslated(
                                  val?.name?.original,
                                  val?.name?.arabic
                                )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
              <div className={styles.Actions}>
                {expandUrl && (
                  <Link
                    className={styles.ActionBtn}
                    to={expandUrl}
                    data-testid="expandUrl-test-id"
                  >
                    <ExpandIcon />
                  </Link>
                )}
                <div className={styles.ActionBtn}>
                  <DotsMenu id={compId} />
                </div>
              </div>
            </div>
          )}

          {(!isLoading &&
            (noData ? (
              <NoData />
            ) : (
              <div data-testid="chart-test-id">
                <Chart
                  options={
                    getBaseLineOptions(
                      theme.colors.chartsColors,
                      data?.xaxis,
                      tooltipContent,
                      removeTooltip,
                      showDefaultTooltip
                    ) as any
                  }
                  series={data?.values}
                  type="line"
                  height="200"
                />
              </div>
            ))) || <Loader />}
        </div>
        <div className="position-relative">{additionalComponents}</div>
      </div>
    </ServerError>
  );
};

export default BaseLine;
const isAr = getFromLocalStorage('LNG') === 'ar';
function getBaseLineOptions(
  colors: string[],
  categories?: string[],
  customTooltip?: any,
  removeTooltip?: boolean,
  showDefaultTooltip?: boolean | undefined
) {
  const options = {
    chart: {
      height: 350,
      type: 'line',
      zoom: {
        type: 'x',
        enabled: true,
      },

      toolbar: {
        autoSelected: 'zoom',
        show: false,
        tools: {
          zoom: false,
          zoomin: false,
          zoomout: false,
        },
      },

      shadow: {
        enabled: true,
        color: '#000',
        top: 18,
        left: 7,
        blur: 10,
        opacity: 1,
      },
    },
    xaxis: {
      opposite: isAr && true,
      show: true,
      categories: categories,
      tooltip: {
        enabled: false,
        formatter: undefined,
      },
      labels: {
        // rotate: -45,
        rotateAlways: false,
        hideOverlappingLabels: false,
        offsetX: isAr ? -8 : 0,
      },
    },
    yaxis: {
      opposite: isAr && true,

      show: true,
      labels: {
        formatter: (value: any) => nFormatter(Math.ceil(value), 2),
        tooltip: {
          enabled: false,
          offsetX: 0,
        },
      },
    },
    colors: colors,
    markers: {
      size: [7, 7],
      strokeWidth: 0,
      shape: 'circle',
      radius: 2,
      hover: {
        sizeOffset: 2,
      },
    },
    stroke: {
      curve: 'straight',
      width: '2',
    },
    legend: {
      show: false,
      position: 'top',
      horizontalAlign: 'right',
      floating: true,
      offsetY: -25,
      offsetX: -5,
    },
    grid: {
      show: false,
      position: 'back',
      xaxis: {
        lines: {
          show: false,
        },
        tooltip: {
          enabled: false,
        },
      },
      yaxis: {
        lines: {
          show: false,
        },
      },
      padding: {
        top: 0,
        right: 0,
        bottom: 10,
        left: 35,
      },
    },
    tooltip: {
      enabled: removeTooltip ? (showDefaultTooltip ? true : false) : true,
      custom: showDefaultTooltip
        ? undefined
        : function ({ seriesIndex, dataPointIndex, tooltipData }: any) {
            return customTooltip(seriesIndex, dataPointIndex, tooltipData);
          },
      intersect: false,
      shared: false,
    },
  };
  return options;
}
